import { useLayoutEffect, type RefObject } from "react";
import type { WorkspaceControlDensity } from "./workspace-control-density.js";

/* The top-chrome inset a corner `WorkspaceControlGroup` publishes, kept out of
 * the component. Internal: not exported from the barrel. */

export const TOP_EDGE_INSET = 16;
export const PHONE_ROW_GAP = 8;
export const FALLBACK_CONTROL_HEIGHT = 40;

/**
 * Publish one measured top-chrome inset on the workspace. Notices, panels, and
 * camera owners inherit the same value instead of guessing at `top-16`. Only a
 * corner group publishes it (`active`); an inline group sits inside a box its
 * parent positions, and that box is the one the inset counts.
 *
 * `density`, `counterpartHeight` and `phoneStacked` are re-publish triggers:
 * each can move the bottom edge of a top row.
 */
export function useWorkspaceTopChromeInset(
  ref: RefObject<HTMLElement | null>,
  active: boolean,
  density: WorkspaceControlDensity,
  counterpartHeight: number | undefined,
  phoneStacked: boolean,
): void {
  useLayoutEffect(() => {
    const node = ref.current;
    const root =
      node?.closest<HTMLElement>("[data-entity-workspace]") ??
      node?.parentElement;
    if (!active || !node || !root) return;
    const report = (): void => {
      const rootTop = root.getBoundingClientRect().top;
      const groups = root.querySelectorAll<HTMLElement>(
        '[data-placement="top-start"], [data-placement="top-end"]',
      );
      let bottom = TOP_EDGE_INSET + FALLBACK_CONTROL_HEIGHT;
      groups.forEach((group) => {
        bottom = Math.max(
          bottom,
          group.getBoundingClientRect().bottom - rootTop,
        );
      });
      root.style.setProperty(
        "--pb-workspace-top-chrome-bottom",
        `${Math.ceil(bottom + PHONE_ROW_GAP)}px`,
      );
    };
    report();
    const topologyObserver =
      typeof MutationObserver === "undefined"
        ? undefined
        : new MutationObserver(report);
    topologyObserver?.observe(root, { childList: true, subtree: true });
    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", report);
      return () => {
        topologyObserver?.disconnect();
        window.removeEventListener("resize", report);
      };
    }
    const observer = new ResizeObserver(report);
    observer.observe(root);
    root
      .querySelectorAll<HTMLElement>(
        '[data-placement="top-start"], [data-placement="top-end"]',
      )
      .forEach((group) => observer.observe(group));
    return () => {
      topologyObserver?.disconnect();
      observer.disconnect();
    };
  }, [active, counterpartHeight, density, phoneStacked, ref]);
}
